/**
 * Affinity checks between a restaurant's public name/website and an Instagram handle.
 * Used to reject auto-scraped false positives (e.g. spam/partner embeds) before display or persist.
 */

const NAME_STOPWORDS = new Set([
  'restaurant',
  'restaurante',
  'restaurants',
  'bar',
  'cafe',
  'cafeteria',
  'bistro',
  'kitchen',
  'cocina',
  'hotel',
  'hostal',
  'taberna',
  'tasca',
  'asador',
  'grill',
  'house',
  'the',
  'and',
  'of',
  'la',
  'el',
  'los',
  'las',
  'les',
  'els',
  'de',
  'del',
  'dels',
  'da',
  'do',
  'y',
  'i',
  'a',
  'al',
  'en',
  'un',
  'una',
  'para',
  'por',
]);

/** Minimum score to accept an auto-resolved or untrusted handle. */
export const INSTAGRAM_HANDLE_AFFINITY_MIN_SCORE = 2;

export function foldInstagramMatchText(raw: string): string {
  return String(raw)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

export function significantRestaurantNameTokens(restaurantName: string): string[] {
  const folded = foldInstagramMatchText(restaurantName).replace(/[^a-z0-9\s]/g, ' ');
  const parts = folded.split(/\s+/).filter(Boolean);
  const tokens: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    if (part.length < 4) continue;
    if (NAME_STOPWORDS.has(part)) continue;
    if (seen.has(part)) continue;
    seen.add(part);
    tokens.push(part);
  }
  return tokens;
}

export function compactRestaurantName(restaurantName: string): string {
  return foldInstagramMatchText(restaurantName).replace(/[^a-z0-9]/g, '');
}

export function websiteDomainLabel(websiteUrl: string | null | undefined): string | null {
  if (!websiteUrl || !String(websiteUrl).trim()) return null;
  try {
    const trimmed = String(websiteUrl).trim();
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const host = new URL(withProtocol).hostname.replace(/^www\./i, '');
    const label = foldInstagramMatchText(host.split('.')[0] ?? '').replace(/[^a-z0-9]/g, '');
    return label.length >= 4 ? label : null;
  } catch {
    return null;
  }
}

/**
 * Higher is better. 0 = no affinity.
 * 2+ is required to accept scraped handles.
 */
export function scoreInstagramHandleRestaurantAffinity(
  handle: string,
  restaurantName: string,
  websiteUrl?: string | null
): number {
  const normalizedHandle = foldInstagramMatchText(handle).replace(/^@+/, '').replace(/[^a-z0-9._]/g, '');
  // Compare without dots/underscores for containment
  const handleCompact = normalizedHandle.replace(/[._]/g, '');
  if (!handleCompact || handleCompact.length < 2) return 0;

  let score = 0;
  const tokens = significantRestaurantNameTokens(restaurantName);
  const compactName = compactRestaurantName(restaurantName);

  for (const token of tokens) {
    if (handleCompact.includes(token) || token.includes(handleCompact)) {
      score = Math.max(score, token.length >= 6 ? 3 : 2);
    }
  }

  if (compactName.length >= 5) {
    if (handleCompact.includes(compactName) || compactName.includes(handleCompact)) {
      score = Math.max(score, 3);
    }
  }

  const domain = websiteDomainLabel(websiteUrl);
  if (domain) {
    const domainRelatedToName =
      tokens.some((token) => domain.includes(token) || token.includes(domain)) ||
      (compactName.length >= 5 &&
        (domain.includes(compactName) || compactName.includes(domain)));
    // Handle matching the website domain only counts when that domain itself
    // looks like the restaurant (blocks randomspam.com → @randomspam).
    if (
      domainRelatedToName &&
      (handleCompact.includes(domain) || domain.includes(handleCompact))
    ) {
      score = Math.max(score, 3);
    }
  }

  return score;
}

export function instagramHandleHasRestaurantAffinity(
  handle: string | null | undefined,
  restaurantName: string | null | undefined,
  websiteUrl?: string | null
): boolean {
  if (!handle?.trim() || !restaurantName?.trim()) return false;
  return (
    scoreInstagramHandleRestaurantAffinity(handle, restaurantName, websiteUrl) >=
    INSTAGRAM_HANDLE_AFFINITY_MIN_SCORE
  );
}

/**
 * Prefer a handle that looks like it belongs to the restaurant; otherwise null.
 */
export function pickBestInstagramHandleForRestaurant(
  handles: string[],
  restaurantName: string,
  websiteUrl?: string | null
): { handle: string; score: number } | null {
  let best: { handle: string; score: number } | null = null;
  for (const raw of handles) {
    const handle = String(raw).replace(/^@+/, '').trim();
    if (!handle) continue;
    const score = scoreInstagramHandleRestaurantAffinity(handle, restaurantName, websiteUrl);
    if (score < INSTAGRAM_HANDLE_AFFINITY_MIN_SCORE) continue;
    if (!best || score > best.score) best = { handle, score };
  }
  return best;
}

/**
 * When the venue website domain itself looks like the restaurant (e.g. gaudillesca.com
 * for "Restaurant la Llesca"), treat that label as an Instagram handle candidate.
 */
export function candidateHandleFromWebsiteDomain(
  websiteUrl: string | null | undefined,
  restaurantName: string
): string | null {
  const domain = websiteDomainLabel(websiteUrl);
  if (!domain) return null;
  if (!instagramHandleHasRestaurantAffinity(domain, restaurantName, websiteUrl)) return null;
  if (!/^[a-z0-9._]{1,30}$/i.test(domain)) return null;
  return domain;
}
